import { formatPLN, getExpenseTotal } from "./finance";
import {
  getKnownPlannedAmount,
  UNKNOWN_PLANNED_AMOUNT_LABEL,
} from "./plannedExpenses";

export const EXPENSE_DELETE_BUTTON_LABEL = "Usuń wydatek";
export const PLANNED_EXPENSE_DELETE_BUTTON_LABEL = "Usuń planowany wydatek";

export function canManageExpenseYear({ isAdmin, isActive, isDraft }) {
  return Boolean(isAdmin && (isActive || isDraft));
}

function expenseName(expense = {}) {
  const occasion = expense.occasion?.trim() || "Wydatek";
  const item = expense.item_or_service?.trim();
  return item ? `${occasion} – ${item}` : occasion;
}

function plannedExpenseName(expense = {}) {
  return expense.occasion?.trim() || expense.name?.trim() || "Planowany wydatek";
}

export function buildExpenseDeleteConfirmation(expense) {
  return `Czy na pewno chcesz trwale usunąć wydatek:\n${expenseName(expense)}\no wartości ${formatPLN(
    getExpenseTotal(expense)
  )}?\n\nTej operacji nie można cofnąć.`;
}

export function buildPlannedExpenseDeleteConfirmation(expense) {
  const amount = getKnownPlannedAmount(expense);
  const amountLine =
    amount === null
      ? UNKNOWN_PLANNED_AMOUNT_LABEL
      : `o wartości ${formatPLN(amount)}`;
  return `Czy na pewno chcesz trwale usunąć planowany wydatek:\n${plannedExpenseName(
    expense
  )}\n${amountLine}?\n\nTej operacji nie można cofnąć.`;
}

export function getExpenseDeleteSuccessMessage(reopenedDocumentCount) {
  return Number(reopenedDocumentCount) > 0
    ? "Wydatek został usunięty. Powiązany dokument wrócił do weryfikacji."
    : "Wydatek został usunięty.";
}

export function getExpenseDeleteErrorMessage(error) {
  const message = error?.message || "";
  if (message.includes("EXPENSE_NOT_FOUND")) {
    return "Wydatek nie istnieje lub został już usunięty.";
  }
  if (message.includes("EXPENSE_SCHOOL_YEAR_NOT_WRITABLE")) {
    return "Nie można usunąć wydatku z roku tylko do odczytu.";
  }
  if (message.includes("EXPENSE_DELETE_ADMIN_REQUIRED")) {
    return "Nie masz uprawnień do usunięcia tego wydatku.";
  }
  return "Nie udało się usunąć wydatku.";
}

export async function confirmAndDeleteExpense({
  supabaseClient,
  expense,
  schoolYearId,
  confirmDelete,
}) {
  if (!confirmDelete(buildExpenseDeleteConfirmation(expense))) {
    return { status: "CANCELLED", reopenedDocumentCount: 0 };
  }

  const { data, error } = await supabaseClient.rpc("delete_expense", {
    p_expense_id: expense.id,
    p_school_year_id: schoolYearId,
  });

  if (error) throw error;

  const reopenedDocumentCount = Number(data?.reopened_document_count ?? 0);
  return {
    status: data?.status || "DELETED",
    reopenedDocumentCount,
    message: getExpenseDeleteSuccessMessage(reopenedDocumentCount),
  };
}

export async function confirmAndDeletePlannedExpense({
  supabaseClient,
  expense,
  schoolYearId,
  confirmDelete,
}) {
  if (!confirmDelete(buildPlannedExpenseDeleteConfirmation(expense))) {
    return { status: "CANCELLED" };
  }

  const { error } = await supabaseClient
    .from("planned_expenses")
    .delete()
    .eq("id", expense.id)
    .eq("school_year_id", schoolYearId);

  if (error) throw error;

  return {
    status: "DELETED",
    message: "Planowany wydatek został usunięty.",
  };
}
